import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Users, Package, FileText, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Dashboard = () => {
  const { getAuthHeader, user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/dashboard/stats`, {
          headers: getAuthHeader()
        });
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    fetchStats();
  }, []);

  const statCards = [
    { label: t('clients'), value: stats?.clients || 0, icon: Users, color: 'bg-blue-500', link: '/finance?tab=clients' },
    { label: t('suppliers'), value: stats?.suppliers || 0, icon: Package, color: 'bg-green-500', link: '/finance?tab=suppliers' },
    { label: t('employees'), value: stats?.employees || 0, icon: Users, color: 'bg-purple-500', link: '/hrm?tab=employees' },
    { label: t('orders'), value: stats?.orders || 0, icon: FileText, color: 'bg-orange-500', link: '/inventory?tab=orders' },
  ];

  return (
    <div className="p-8 space-y-8">
      <div>
        <h1 className="text-4xl font-bold tracking-tight" lang={language === 'ar' ? 'ar' : 'en'} data-testid="dashboard-title">
          {t('dashboard')}
        </h1>
        <p className="text-muted-foreground mt-2" lang={language === 'ar' ? 'ar' : 'en'}>{t('overviewSubtitle')}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <Card
              key={idx}
              className="p-6 border border-border hover:border-slate-300 transition-colors cursor-pointer"
              data-testid={`stat-card-${idx}`}
              onClick={() => navigate(stat.link)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>{stat.label}</p>
                  <p className="text-3xl font-bold mt-2 font-mono-numeric">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 rounded-md ${stat.color} flex items-center justify-center`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 border border-border">
          <h3 className="text-xl font-semibold mb-4" lang={language === 'ar' ? 'ar' : 'en'}>{t('orderStatus')}</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm" lang={language === 'ar' ? 'ar' : 'en'}>{t('pending')}</span>
              <span className="font-mono-numeric font-semibold">{stats?.pending_orders || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm" lang={language === 'ar' ? 'ar' : 'en'}>{t('completed')}</span>
              <span className="font-mono-numeric font-semibold">{stats?.completed_orders || 0}</span>
            </div>
          </div>
        </Card>

        <Card className="p-6 border border-border">
          <h3 className="text-xl font-semibold mb-4" lang={language === 'ar' ? 'ar' : 'en'}>{t('financialOverview')}</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm" lang={language === 'ar' ? 'ar' : 'en'}>{t('totalCredit')}</span>
              <span className="font-mono-numeric font-semibold text-green-600">{stats?.total_credit?.toFixed(2) || '0.00'} {t('currency')}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm" lang={language === 'ar' ? 'ar' : 'en'}>{t('totalDebit')}</span>
              <span className="font-mono-numeric font-semibold text-red-600">{stats?.total_debit?.toFixed(2) || '0.00'} {t('currency')}</span>
            </div>
          </div>
        </Card>
      </div>

    </div>
  );
};